import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    TextField,
    Card,
    CardContent,
    CardHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    Chip,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Autocomplete,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Receipt as ReceiptIcon,
    AccountCircle as CustomerIcon,
    Print as PrintIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const TransactionEntry = () => {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
    const [customerSearchText, setCustomerSearchText] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formData, setFormData] = useState({
        customerId: '',
        serial_Number: '',
        refill_Booling_Number: '',
        cash_Memo_Number: '',
        deliveryDate: new Date().toISOString().split('T')[0]
    });
    
    // Print dialog state
    const [printDialogOpen, setPrintDialogOpen] = useState(false);
    const [selectedTransactionForPrint, setSelectedTransactionForPrint] = useState(null);
    const [printRowNumber, setPrintRowNumber] = useState('');
    const [availablePrintSettings, setAvailablePrintSettings] = useState([]);
    const [selectedPrintSetting, setSelectedPrintSetting] = useState('');
    const [loadingPrintSettings, setLoadingPrintSettings] = useState(false);
    
    // Edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedTransactionForEdit, setSelectedTransactionForEdit] = useState(null);
    const [editFormData, setEditFormData] = useState({
        serial_Number: '',
        refill_Booling_Number: '',
        cash_Memo_Number: '',
        deliveryDate: ''
    });
    
    const { hasPermission, user } = useAuth();

    useEffect(() => {
        fetchTransactions();
        

    }, []);

    const searchCustomers = async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setCustomers([]);
            return;
        }

        try {
            setCustomerSearchLoading(true);
            const response = await api.get(`/api/customers?search=${encodeURIComponent(searchTerm)}`);
            setCustomers(response.data);
        } catch (error) {
            console.error('Error searching customers:', error);
            setError('Failed to search customers');
            setCustomers([]);
        } finally {
            setCustomerSearchLoading(false);
        }
    };

    // Debounced search function
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (customerSearchText) {
                searchCustomers(customerSearchText);
            } else {
                setCustomers([]);
            }
        }, 300); // 300ms delay

        return () => clearTimeout(debounceTimer);
    }, [customerSearchText]);

    const fetchTransactions = async () => {
        try {
            const response = await api.get('/api/gastransactions');
            setTransactions(response.data);
        } catch (error) {
            console.error('Error fetching transactions:', error);
            // For now, set empty array to prevent blocking the UI
            setTransactions([]);
            setError('Failed to fetch transactions - this may be due to database schema changes');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError('');

            const transactionData = {
                CustomerId: selectedCustomer ? selectedCustomer.id : parseInt(formData.customerId),
                TenantId: user?.tenantId || 1, // Get from user context, default to 1
                Serial_Number: formData.serial_Number,
                Refill_Booling_Number: formData.refill_Booling_Number,
                Cash_Memo_Number: formData.cash_Memo_Number,
                DeliveryDate: formData.deliveryDate
            };

            await api.post('/api/gastransactions', transactionData);

            setSuccess('Transaction added successfully');
            fetchTransactions();
            resetForm();
        } catch (error) {
            console.error('Error saving transaction:', error);
            setError(error.response?.data?.message || 'Error saving transaction');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            customerId: '',
            serial_Number: '',
            refill_Booling_Number: '',
            cash_Memo_Number: '',
            deliveryDate: new Date().toISOString().split('T')[0]
        });
        setSelectedCustomer(null);
        setCustomerSearchText('');
        setCustomers([]);
    };

    const deleteTransaction = async (id) => {
        if (window.confirm('Are you sure you want to delete this transaction?')) {
            try {
                setLoading(true);
                await api.delete(`/api/gastransactions/${id}`);
                setSuccess('Transaction deleted successfully');
                fetchTransactions();
            } catch (error) {
                console.error('Error deleting transaction:', error);
                setError('Error deleting transaction');
            } finally {
                setLoading(false);
            }
        }
    };

    // Edit transaction functionality
    const handleEditTransaction = (transaction) => {
        setSelectedTransactionForEdit(transaction);
        setEditFormData({
            serial_Number: transaction.serial_Number || '',
            refill_Booling_Number: transaction.refill_Booling_Number || '',
            cash_Memo_Number: transaction.cash_Memo_Number || '',
            deliveryDate: transaction.deliveryDate ? new Date(transaction.deliveryDate).toISOString().split('T')[0] : ''
        });
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setSelectedTransactionForEdit(null);
        setEditFormData({
            serial_Number: '',
            refill_Booling_Number: '',
            cash_Memo_Number: '',
            deliveryDate: ''
        });
    };

    const handleEditFormChange = (field, value) => {
        setEditFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleUpdateTransaction = async () => {
        if (!selectedTransactionForEdit) return;

        try {
            setLoading(true);
            setError('');

            // Prepare update data - only send fields that have changed
            const updateData = {};
            
            if (editFormData.serial_Number !== (selectedTransactionForEdit.serial_Number || '')) {
                updateData.Serial_Number = editFormData.serial_Number;
            }
            
            if (editFormData.refill_Booling_Number !== (selectedTransactionForEdit.refill_Booling_Number || '')) {
                updateData.Refill_Booling_Number = editFormData.refill_Booling_Number;
            }
            
            if (editFormData.cash_Memo_Number !== (selectedTransactionForEdit.cash_Memo_Number || '')) {
                updateData.Cash_Memo_Number = editFormData.cash_Memo_Number;
            }
            
            const originalDate = selectedTransactionForEdit.deliveryDate ? 
                new Date(selectedTransactionForEdit.deliveryDate).toISOString().split('T')[0] : '';
            if (editFormData.deliveryDate !== originalDate) {
                updateData.DeliveryDate = editFormData.deliveryDate;
            }

            // Only make the API call if there are changes
            if (Object.keys(updateData).length === 0) {
                setError('No changes detected');
                return;
            }

            await api.put(`/api/gastransactions/${selectedTransactionForEdit.id}`, updateData);

            setSuccess('Transaction updated successfully');
            fetchTransactions();
            handleCloseEditDialog();
        } catch (error) {
            console.error('Error updating transaction:', error);
            if (error.response?.data?.errors) {
                const validationErrors = Object.values(error.response.data.errors).flat();
                setError(`Validation errors: ${validationErrors.join(', ')}`);
            } else {
                setError(error.response?.data?.message || 'Error updating transaction');
            }
        } finally {
            setLoading(false);
        }
    };

    // Print functionality
    const handlePrintTransaction = async (transaction) => {
        setSelectedTransactionForPrint(transaction);
        setPrintDialogOpen(true);
        setPrintRowNumber('');
        setSelectedPrintSetting('');
        
        // Load available print settings
        await loadAvailablePrintSettings();
    };

    const loadAvailablePrintSettings = async () => {
        setLoadingPrintSettings(true);
        try {
            const response = await api.get('/api/transactionsettings/names');
            const settingsNames = response.data || [];
            setAvailablePrintSettings(settingsNames);
            
            // Auto-select first setting if available
            if (settingsNames.length > 0) {
                const firstSetting = settingsNames[0];
                setSelectedPrintSetting(firstSetting);
                loadSettingsDetails(firstSetting);
            }
        } catch (error) {
            console.error('Error loading print settings:', error);
            setAvailablePrintSettings([]);
            setError('Could not load print settings. Using default format.');
        } finally {
            setLoadingPrintSettings(false);
        }
    };

    const handleClosePrintDialog = () => {
        setPrintDialogOpen(false);
        setSelectedTransactionForPrint(null);
        setPrintRowNumber('');
        setSelectedPrintSetting('');
        setAvailablePrintSettings([]);
        setCurrentSettingsDetails(null);
    };

    // Function to get a quick preview of the selected setting
    const getSettingPreviewText = (settingName) => {
        if (!settingName) return "Default print format (12px font, 25px row height, max 25 rows)";
        return `Custom format: ${settingName}`;
    };

    // Function to get detailed settings info for display
    const [currentSettingsDetails, setCurrentSettingsDetails] = useState(null);

    const loadSettingsDetails = async (settingName) => {
        if (!settingName) {
            setCurrentSettingsDetails(null);
            return;
        }

        try {
            const response = await api.get(`/api/transactionsettings/${settingName}`);
            const settings = response.data;
            setCurrentSettingsDetails({
                fontSize: settings.fontSize || 12,
                rowHeight: settings.rowHeight || 25,
                rowsPerPage: settings.rowsPerPage || 25,
                startingTop: settings.startingTop || 100,
                leftMargin: settings.leftMargin || 50
            });
        } catch (error) {
            console.error('Error loading settings details:', error);
            setCurrentSettingsDetails(null);
        }
    };

    const handlePrintConfirm = async () => {
        const rowNumber = parseInt(printRowNumber);
        
        if (!selectedTransactionForPrint || !printRowNumber || isNaN(rowNumber) || rowNumber < 1) {
            setError('Please enter a valid row number (minimum 1)');
            return;
        }

        try {
            let transactionSettings = null;
            
            // Load the selected print setting if available
            if (selectedPrintSetting) {
                const settingsResponse = await api.get(`/api/transactionsettings/${selectedPrintSetting}`);
                transactionSettings = settingsResponse.data;
            }

            // Validate row number against max rows per page setting
            if (transactionSettings) {
                const maxRowsPerPage = transactionSettings.rowsPerPage || 25; // Default to 25 if not set
                
                if (rowNumber > maxRowsPerPage) {
                    setError(`Row number ${rowNumber} exceeds maximum rows per page (${maxRowsPerPage}) for the selected print setting "${selectedPrintSetting}". Please enter a row number between 1 and ${maxRowsPerPage}.`);
                    return;
                }
            } else {
                // Default validation for basic format (max 25 rows)
                if (rowNumber > 25) {
                    setError('Row number cannot exceed 25 for default format. Please enter a row number between 1 and 25.');
                    return;
                }
            }

            // Generate print content using selected settings or default values
            const printContent = transactionSettings 
                ? generateTransactionPrintHTML(selectedTransactionForPrint, transactionSettings, rowNumber)
                : generateBasicTransactionPrintHTML(selectedTransactionForPrint, rowNumber);
            
            // Open print window
            const printWindow = window.open('', '_blank', 'width=800,height=600');
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();

            // Close dialog
            handleClosePrintDialog();
            setSuccess(`Transaction printed successfully${selectedPrintSetting ? ` using "${selectedPrintSetting}" settings` : ' using default format'} at row ${rowNumber}`);

        } catch (error) {
            console.error('Error printing transaction:', error);
            setError('Error loading print settings. Using default format.');
            
            // Fallback to basic print without settings (with basic validation)
            if (rowNumber > 25) {
                setError('Row number cannot exceed 25. Please enter a valid row number.');
                return;
            }
            
            const basicPrintContent = generateBasicTransactionPrintHTML(selectedTransactionForPrint, rowNumber);
            const printWindow = window.open('', '_blank', 'width=800,height=600');
            printWindow.document.write(basicPrintContent);
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();

            // Close dialog and reset state
            handleClosePrintDialog();
        }
    };

    const generateTransactionPrintHTML = (transaction, settings, rowNumber) => {
        // Use settings if available, otherwise use defaults
        const fontSize = settings?.fontSize || 12;
        const rowHeight = settings?.rowHeight || 25;
        const startingTop = settings?.startingTop || 100;
        const leftMargin = settings?.leftMargin || 50;
        
        // Parse field settings if available
        let fieldSettings = {};
        if (settings?.fieldSettings) {
            try {
                fieldSettings = typeof settings.fieldSettings === 'string' 
                    ? JSON.parse(settings.fieldSettings)
                    : settings.fieldSettings;
            } catch (e) {
                console.error('Error parsing field settings:', e);
            }
        }

        // Default field positions if no settings available
        const defaultFieldSettings = {
            serialNumber: { left: 0, width: 100, visible: true },
            refillBookingNo: { left: 110, width: 150, visible: true },
            cashMemoNo: { left: 270, width: 130, visible: true },
            deliveryDate: { left: 410, width: 120, visible: true }
        };

        const finalFieldSettings = Object.keys(defaultFieldSettings).length > 0 && fieldSettings.serialNumber 
            ? fieldSettings 
            : defaultFieldSettings;

        // Calculate position for the specific row
        const topPosition = startingTop + ((rowNumber - 1) * rowHeight);

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Gas Passbook - Transaction Print</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                        padding: ${leftMargin}px;
                        font-size: ${fontSize}px;
                        position: relative;
                    }
                    .transaction-row {
                        position: absolute;
                        width: 100%;
                        height: ${rowHeight}px;
                        line-height: ${rowHeight}px;
                        top: ${topPosition}px;
                    }
                    .field {
                        position: absolute;
                        overflow: hidden;
                        white-space: nowrap;
                    }
                    @media print {
                        body { 
                            margin: 0; 
                            padding: ${leftMargin}px;
                        }
                        @page {
                            margin: 0;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="transaction-row">
                    ${finalFieldSettings.serialNumber?.visible ? `
                        <div class="field" style="left: ${finalFieldSettings.serialNumber.left}px; width: ${finalFieldSettings.serialNumber.width}px;">
                            ${transaction.serial_Number || ''}
                        </div>
                    ` : ''}
                    ${finalFieldSettings.refillBookingNo?.visible ? `
                        <div class="field" style="left: ${finalFieldSettings.refillBookingNo.left}px; width: ${finalFieldSettings.refillBookingNo.width}px;">
                            ${transaction.refill_Booling_Number || ''}
                        </div>
                    ` : ''}
                    ${finalFieldSettings.cashMemoNo?.visible ? `
                        <div class="field" style="left: ${finalFieldSettings.cashMemoNo.left}px; width: ${finalFieldSettings.cashMemoNo.width}px;">
                            ${transaction.cash_Memo_Number || ''}
                        </div>
                    ` : ''}
                    ${finalFieldSettings.deliveryDate?.visible ? `
                        <div class="field" style="left: ${finalFieldSettings.deliveryDate.left}px; width: ${finalFieldSettings.deliveryDate.width}px;">
                            ${new Date(transaction.deliveryDate).toLocaleDateString()}
                        </div>
                    ` : ''}
                </div>
            </body>
            </html>
        `;
    };

    const generateBasicTransactionPrintHTML = (transaction, rowNumber) => {
        const rowHeight = 25;
        const startingTop = 100;
        const leftMargin = 50;
        const topPosition = startingTop + ((rowNumber - 1) * rowHeight);

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Gas Passbook - Transaction Print (Basic)</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                        padding: ${leftMargin}px;
                        font-size: 12px;
                        position: relative;
                    }
                    .transaction-row {
                        position: absolute;
                        width: 100%;
                        height: ${rowHeight}px;
                        line-height: ${rowHeight}px;
                        top: ${topPosition}px;
                    }
                    .field {
                        position: absolute;
                        overflow: hidden;
                        white-space: nowrap;
                    }
                    @media print {
                        body { margin: 0; padding: ${leftMargin}px; }
                        @page { margin: 0; }
                    }
                </style>
            </head>
            <body>
                <div class="transaction-row">
                    <div class="field" style="left: 0px; width: 100px;">${transaction.serial_Number || ''}</div>
                    <div class="field" style="left: 110px; width: 150px;">${transaction.refill_Booling_Number || ''}</div>
                    <div class="field" style="left: 270px; width: 130px;">${transaction.cash_Memo_Number || ''}</div>
                    <div class="field" style="left: 410px; width: 120px;">${new Date(transaction.deliveryDate).toLocaleDateString()}</div>
                </div>
            </body>
            </html>
        `;
    };

    const handleInputChange = (field) => (event) => {
        setFormData({ ...formData, [field]: event.target.value });
    };

    const getCustomerName = (customerId) => {
        const customer = customers.find(c => c.id === customerId);
        return customer ? customer.consumer_Name || `Customer ${customer.id}` : '';
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Gas Transaction Entry
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}
            </Box>

            {hasPermission('transactions.create') && (
                <Card sx={{ mb: 4 }}>
                    <CardHeader
                        avatar={<ReceiptIcon />}
                        title={
                            <Typography variant="h6">
                                Add New Transaction
                            </Typography>
                        }
                    />
                    <CardContent>
                        <Box component="form" onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        sx={{ minWidth: 300 }}
                                        options={customers}
                                        getOptionLabel={(option) => `${option.consumerNumber || option.ConsumerNumber} - ${option.consumer_Name || option.Consumer_Name}`}
                                        value={selectedCustomer}
                                        onChange={(event, newValue) => {
                                            setSelectedCustomer(newValue);
                                            setFormData(prev => ({
                                                ...prev,
                                                customerId: newValue ? newValue.id : ''
                                            }));
                                        }}
                                        inputValue={customerSearchText}
                                        onInputChange={(event, newInputValue) => {
                                            setCustomerSearchText(newInputValue);
                                        }}
                                        loading={customerSearchLoading}
                                        loadingText="Searching customers..."
                                        noOptionsText={customerSearchText.length < 2 ? "Type at least 2 characters to search" : "No customers found"}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Search Customer"
                                                placeholder="Type consumer name, number, or card serial..."
                                                variant="outlined"
                                                required
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start">
                                                                <CustomerIcon />
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                    endAdornment: (
                                                        <>
                                                            {customerSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props}>
                                                <Box>
                                                    <Typography variant="body1">
                                                        {option.consumer_Name || option.Consumer_Name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Consumer: {option.consumerNumber || option.ConsumerNumber} | 
                                                        Card: {option.cardSerialNumber || option.CardSerialNumber || 'N/A'} |
                                                        SV: {option.sV_Number || option.sv_Number || option.SV_Number || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        filterOptions={(x) => x} // Disable client-side filtering since we're doing server-side search
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Delivery Date"
                                        type="date"
                                        value={formData.deliveryDate}
                                        onChange={handleInputChange('deliveryDate')}
                                        required
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Serial Number"
                                        value={formData.serial_Number}
                                        onChange={handleInputChange('serial_Number')}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Refill Booking Number"
                                        value={formData.refill_Booling_Number}
                                        onChange={handleInputChange('refill_Booling_Number')}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Cash Memo Number"
                                        value={formData.cash_Memo_Number}
                                        onChange={handleInputChange('cash_Memo_Number')}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        disabled={loading}
                                        size="large"
                                    >
                                        Add Transaction
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            )}

            <Paper>
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Recent Transactions
                        <Chip 
                            label={`${transactions.length} transactions`} 
                            color="primary" 
                            size="small" 
                            sx={{ ml: 2 }}
                        />
                    </Typography>
                    
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Delivery Date</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Customer</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Serial Number</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Refill Booking Number</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Cash Memo Number</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Print</TableCell>
                                    {(() => {
                                        const hasUpdatePerm = hasPermission('transactions.edit');
                                        const hasDeletePerm = hasPermission('transactions.delete');
                                        return (hasUpdatePerm || hasDeletePerm);
                                    })() && (
                                        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Actions</TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.slice(0, 20).map((transaction) => (
                                    <TableRow key={transaction.id} hover>
                                        <TableCell>
                                            {new Date(transaction.deliveryDate).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>{transaction.customerName || 'N/A'}</TableCell>
                                        <TableCell>{transaction.serial_Number || '-'}</TableCell>
                                        <TableCell>{transaction.refill_Booling_Number || '-'}</TableCell>
                                        <TableCell>{transaction.cash_Memo_Number || '-'}</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                color="primary"
                                                onClick={() => handlePrintTransaction(transaction)}
                                                size="small"
                                                title="Print Transaction"
                                            >
                                                <PrintIcon />
                                            </IconButton>
                                        </TableCell>
                                        {(() => {
                                            const hasUpdatePerm = hasPermission('transactions.edit');
                                            const hasDeletePerm = hasPermission('transactions.delete');
                                            return (hasUpdatePerm || hasDeletePerm);
                                        })() && (
                                            <TableCell align="center">
                                                {(() => {
                                                    const hasUpdatePerm = hasPermission('transactions.edit');
                                                    return hasUpdatePerm;
                                                })() && (
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleEditTransaction(transaction)}
                                                        size="small"
                                                        title="Edit Transaction"
                                                        sx={{ mr: 0.5 }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                )}
                                                {hasPermission('transactions.delete') && (
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => deleteTransaction(transaction.id)}
                                                        size="small"
                                                        title="Delete Transaction"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                {transactions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={(hasPermission('transactions.edit') || hasPermission('transactions.delete')) ? 7 : 6} align="center">
                                            <Typography color="textSecondary">
                                                No transactions found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            {/* Print Dialog */}
            <Dialog open={printDialogOpen} onClose={handleClosePrintDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Print Transaction</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Typography variant="body1" gutterBottom>
                            Transaction Details:
                        </Typography>
                        {selectedTransactionForPrint && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2"><strong>Customer:</strong> {selectedTransactionForPrint.customerName || 'N/A'}</Typography>
                                <Typography variant="body2"><strong>Serial Number:</strong> {selectedTransactionForPrint.serial_Number || '-'}</Typography>
                                <Typography variant="body2"><strong>Refill Booking:</strong> {selectedTransactionForPrint.refill_Booling_Number || '-'}</Typography>
                                <Typography variant="body2"><strong>Cash Memo:</strong> {selectedTransactionForPrint.cash_Memo_Number || '-'}</Typography>
                                <Typography variant="body2"><strong>Delivery Date:</strong> {selectedTransactionForPrint.deliveryDate ? new Date(selectedTransactionForPrint.deliveryDate).toLocaleDateString() : '-'}</Typography>
                            </Box>
                        )}
                        
                        {/* Print Settings Selection */}
                        <Box sx={{ mb: 3 }}>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Print Settings Template</InputLabel>
                                <Select
                                    value={selectedPrintSetting}
                                    label="Print Settings Template"
                                    onChange={(e) => {
                                        const newSetting = e.target.value;
                                        setSelectedPrintSetting(newSetting);
                                        loadSettingsDetails(newSetting);
                                    }}
                                    disabled={loadingPrintSettings}
                                >
                                    {availablePrintSettings.length === 0 && !loadingPrintSettings && (
                                        <MenuItem value="" disabled>
                                            No saved print settings available
                                        </MenuItem>
                                    )}
                                    {availablePrintSettings.map((settingName) => (
                                        <MenuItem key={settingName} value={settingName}>
                                            {settingName}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {loadingPrintSettings && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <CircularProgress size={16} sx={{ mr: 1 }} />
                                        <Typography variant="caption" color="textSecondary">
                                            Loading print settings...
                                        </Typography>
                                    </Box>
                                )}
                                {availablePrintSettings.length === 0 && !loadingPrintSettings && (
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                        No saved print settings found. Default formatting will be used.
                                        <br />
                                        Create print settings in "Passbook Transaction Print Settings" page.
                                    </Typography>
                                )}
                            </FormControl>
                            
                            {/* Settings Preview */}
                            {(selectedPrintSetting || availablePrintSettings.length === 0) && (
                                <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="caption" color="textSecondary" gutterBottom sx={{ display: 'block' }}>
                                        Print Format Details:
                                    </Typography>
                                    {currentSettingsDetails ? (
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main', mb: 1 }}>
                                                {selectedPrintSetting}
                                            </Typography>
                                            <Grid container spacing={1} sx={{ fontSize: '0.875rem' }}>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary">Font Size:</Typography>
                                                    <Typography variant="body2">{currentSettingsDetails.fontSize}px</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary">Row Height:</Typography>
                                                    <Typography variant="body2">{currentSettingsDetails.rowHeight}px</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary">Max Rows:</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: currentSettingsDetails.rowsPerPage < 25 ? 'warning.main' : 'success.main' }}>
                                                        {currentSettingsDetails.rowsPerPage}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="caption" color="textSecondary">Left Margin:</Typography>
                                                    <Typography variant="body2">{currentSettingsDetails.leftMargin}px</Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2">
                                            Default format (12px font, 25px row height, max 25 rows)
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>

                        <TextField
                            label="Row Number (Position on passbook page)"
                            type="number"
                            value={printRowNumber}
                            onChange={(e) => setPrintRowNumber(e.target.value)}
                            fullWidth
                            helperText={`Enter the row number (1 to ${currentSettingsDetails?.rowsPerPage || 25}) where you want to print this transaction`}
                            inputProps={{ 
                                min: 1, 
                                max: currentSettingsDetails?.rowsPerPage || 25 
                            }}
                            error={printRowNumber && (parseInt(printRowNumber) > (currentSettingsDetails?.rowsPerPage || 25) || parseInt(printRowNumber) < 1)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Box sx={{ flexGrow: 1 }}>
                        {selectedPrintSetting && currentSettingsDetails && (
                            <Typography variant="caption" color="primary">
                                Using: {selectedPrintSetting} (Max rows: {currentSettingsDetails.rowsPerPage})
                            </Typography>
                        )}
                        {!selectedPrintSetting && availablePrintSettings.length === 0 && (
                            <Typography variant="caption" color="textSecondary">
                                Using default format (Max rows: 25)
                            </Typography>
                        )}
                    </Box>
                    <Button onClick={handleClosePrintDialog}>Cancel</Button>
                    <Button 
                        onClick={handlePrintConfirm} 
                        variant="contained" 
                        disabled={!printRowNumber || loadingPrintSettings || (printRowNumber && (parseInt(printRowNumber) > (currentSettingsDetails?.rowsPerPage || 25) || parseInt(printRowNumber) < 1))}
                        startIcon={<PrintIcon />}
                    >
                        Print
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Transaction Dialog */}
            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Transaction</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        {selectedTransactionForEdit && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Customer: {selectedTransactionForEdit.customerName || 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Transaction ID: {selectedTransactionForEdit.id}
                                </Typography>
                            </Box>
                        )}
                        
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Serial Number"
                                    value={editFormData.serial_Number}
                                    onChange={(e) => handleEditFormChange('serial_Number', e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    label="Refill Booking Number"
                                    value={editFormData.refill_Booling_Number}
                                    onChange={(e) => handleEditFormChange('refill_Booling_Number', e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    label="Cash Memo Number"
                                    value={editFormData.cash_Memo_Number}
                                    onChange={(e) => handleEditFormChange('cash_Memo_Number', e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    label="Delivery Date"
                                    type="date"
                                    value={editFormData.deliveryDate}
                                    onChange={(e) => handleEditFormChange('deliveryDate', e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                        </Grid>
                        
                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseEditDialog} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpdateTransaction} 
                        variant="contained" 
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} /> : <EditIcon />}
                    >
                        {loading ? 'Updating...' : 'Update Transaction'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default TransactionEntry;